import { Component, computed, effect, inject, input } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FieldTree, FormField } from '@angular/forms/signals';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { of } from 'rxjs';
import { MceApiService } from '../../../core/mceapi.client.generated';
import { BS7671_STANDARD_GUID, ERA6930P3_STANDARD_GUID, ERA6930P5_STANDARD_GUID, IEC60502P2_STANDARD_GUID, UDR_STANDARD_GUID } from '../../../shared/constants';
import { EditCable } from '../cable-edit.model';
import { Load } from '../load/load.model';
import { Protection } from '../protection/cable-protection.component';
import { CableSettings } from '../settings/cable-settings';
import { createSystemVoltageDto, SystemVoltage } from '../system-voltage/system-voltage.model';
import { CapacityCalculationComponent } from './capacity-calculation/capacity-calculation.component';
import { FactorsBs7671Component } from './factors-bs7671/factors-bs7671.component';
import { FactorsEra6930p3Component } from './factors-era6930p3/factors-era6930p3.component';
import { FactorsEra6930p5Component } from './factors-era6930p5/factors-era6930p5.component';
import { FactorsIec60502p2Component } from './factors-iec60502p2/factors-iec60502p2.component';
import { FactorsUdrComponent } from './factors-udr/factors-udr.component';
import { SingleCoreArrangementComponent } from './single-core-arrangement/single-core-arrangement.component';


@Component({
    selector: 'cable-edit-installation',
  imports: [FormField, MatSelectModule, MatInputModule, 
    SingleCoreArrangementComponent,  
    FactorsUdrComponent, FactorsBs7671Component, FactorsEra6930p3Component, FactorsEra6930p5Component, FactorsIec60502p2Component,
    CapacityCalculationComponent,

  ],
    templateUrl: './cable-installation.component.html',

})
export class CableInstallationComponent {

  readonly form = input.required<FieldTree<EditCable>>();

  private mceApiService = inject(MceApiService);

  standardsRx = rxResource({
    params: () => ({
      libraryGuid: this.form().selectedCable.libraryGuid().value(),
      voltage: createSystemVoltageDto(this.form().systemVoltage)(),
      valid: this.form().systemVoltage().valid() && this.form().selectedCable().valid()
    }),
    stream: ({ params }) =>
      params.valid
        ? this.mceApiService.v1CableCapacityStandardsByLibrary({
          libraryGuid: params.libraryGuid,
          systemVoltage: params.voltage
        })
        : of([]),
  });
  standards = computed(() => this.standardsRx.value() ?? []);

  isSingleCoreRx = rxResource({
    params: () => ({
      systemVoltage: createSystemVoltageDto(this.form().systemVoltage)(),
      libraryGuid: this.form().selectedCable.libraryGuid().value(),
      valid: this.form().systemVoltage().valid() && this.form().selectedCable().valid()
    }),
    stream: ({ params }) =>
      params.valid
        ? this.mceApiService.v1CableLibrarySinglecore(params.libraryGuid, params.systemVoltage)
        : of({value: false})
  });
  isSingleCore = computed(() => this.isSingleCoreRx.value()?.value ?? false);

  viewState = computed(() => ({

    showForm: this.form().selectedCable().valid(),

    showSingleCoreArrangement: !this.isSingleCoreRx.isLoading() && this.isSingleCore(),
    showAmbientTemperature: this.form().installation.standardGuid().value() !== UDR_STANDARD_GUID,
    showGrouping: this.form().installation.standardGuid().value() !== UDR_STANDARD_GUID,

    showUDRFactors: this.form().installation.standardGuid().value() === UDR_STANDARD_GUID,
    showBS7671Factors: this.form().installation.standardGuid().value() === BS7671_STANDARD_GUID,
    showERA6930P3Factors: this.form().installation.standardGuid().value() === ERA6930P3_STANDARD_GUID,
    showERA6930P5Factors: this.form().installation.standardGuid().value() === ERA6930P5_STANDARD_GUID,
    showIEC60502P2Factors: this.form().installation.standardGuid().value() === IEC60502P2_STANDARD_GUID,

  }));


  resetStandard = effect(() => {
    const standardGuid = this.form().installation.standardGuid().value();
    const standards = this.standards();
    if (standards.length === 0) return; // skip while loading or empty
    if (!standards.some(s => s.guid === standardGuid)) { // && this.form.installation.standardGuid().dirty()) {
      const firstStandardGuid = standards[0].guid;
      if (firstStandardGuid !== undefined) {
        this.form().installation.standardGuid().value.set(firstStandardGuid);
      }
    }
  });

  resetSingleCoreConfigurationRequired = effect(() => {
    const required = this.form().installation.singleCoreArrangement.required();
    if (required.value() !== this.isSingleCore() && !this.isSingleCoreRx.isLoading())
      this.form().installation.singleCoreArrangement.required().value.set(this.isSingleCore());

  });


}

